export interface NotificationStreamEvent {
  clinicId: string;
  revision: number;
  eventType: string;
  occurredAt: string;
}

interface NotificationEventConnectionOptions {
  baseUrl: string;
  accessToken: string;
  clinicId: string;
  activeRole: string;
  signal: AbortSignal;
  lastEventId?: number;
  fetcher?: typeof fetch;
  onOpen?: () => void;
  onEvent: (event: NotificationStreamEvent) => void;
}

export async function connectNotificationEvents(options: NotificationEventConnectionOptions): Promise<void> {
  const fetcher = options.fetcher ?? fetch;
  const headers: Record<string, string> = {
    Accept: 'text/event-stream',
    Authorization: `Bearer ${options.accessToken}`,
    'X-Clinic-Id': options.clinicId,
    'X-Active-Role': options.activeRole,
  };
  if (options.lastEventId != null) headers['Last-Event-ID'] = String(options.lastEventId);

  const response = await fetcher(`${options.baseUrl.replace(/\/$/, '')}/clinic/notifications/events`, {
    headers,
    signal: options.signal,
  });
  if (!response.ok || !response.body) throw new Error(`Notification event stream failed (${response.status})`);
  options.onOpen?.();

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!options.signal.aborted) {
      // Stream reads must remain sequential for this reader.
      // oxlint-disable-next-line eslint/no-await-in-loop
      const { done, value } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      let boundary = buffer.indexOf('\n\n');
      while (boundary >= 0) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const data = block.split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trimStart())
          .join('\n');
        if (data) options.onEvent(JSON.parse(data) as NotificationStreamEvent);
        if (options.signal.aborted) return;
        boundary = buffer.indexOf('\n\n');
      }
    }
  } finally {
    void reader.cancel().catch(() => undefined);
  }
}
